import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

import {
  GridApi,
  GridColDef,
  GridColumnVisibilityModel,
  GridPinnedColumnFields,
} from '@mui/x-data-grid-premium';

import { HeaderResponse } from '@/api/data-contracts';

import {
  buildHeaderRequest,
  buildPinnedColumnsFromResponse,
  buildVisibilityModelFromResponse,
  canonicalizeHeaderRequest,
  extractColumnOrderFromApi,
  fromBackendField,
  projectResponseAsRequest,
} from './headersApiAdapter';
import useTablesService from './useTablesService';

const SAVE_DEBOUNCE_MS = 1000;

export interface UseHeadersBackendSyncParams {
  headersRequest: { pageType: string; resourceId?: string } | undefined;
  processedColumns: GridColDef[];
  effectiveColumnVisibilityModel: Record<string, boolean>;
  apiRef: MutableRefObject<GridApi>;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  groupByField?: string | null;
  onGroupByChange?: (field: string | null) => void;
  pinnedColumns?: GridPinnedColumnFields;
  onPinnedColumnsChange?: (pinned: GridPinnedColumnFields) => void;
  mandatoryPinnedColumns?: GridPinnedColumnFields;
}

export interface UseHeadersBackendSyncReturn {
  savedOrder: string[] | null;
  setDragOrder: (order: string[] | null) => void;
  dragOrder: string[] | null;
  isHeadersLoading: boolean;
  scheduleSave: () => void;
}

const useHeadersBackendSync = ({
  headersRequest,
  processedColumns,
  effectiveColumnVisibilityModel,
  apiRef,
  onColumnVisibilityModelChange,
  groupByField,
  onGroupByChange,
  pinnedColumns,
  onPinnedColumnsChange,
  mandatoryPinnedColumns,
}: UseHeadersBackendSyncParams): UseHeadersBackendSyncReturn => {
  const { listHeaders, addHeader, updateHeader } = useTablesService();

  const [savedOrder, setSavedOrder] = useState<string[] | null>(null);
  const [dragOrder, setDragOrder] = useState<string[] | null>(null);
  const [isHeadersLoading, setIsHeadersLoading] = useState<boolean>(!!headersRequest);

  const savedHeaderIdRef = useRef<string | null>(null);
  // Fingerprint of our last confirmed write. saveToApi skips writes whose content
  // matches the server: the /headers row is one shared per-user record and update()
  // replaces it wholesale, so a same-content write is a read-modify-write hazard (it
  // can clobber a newer row from another session/tab), not a harmless no-op.
  const lastSyncedRef = useRef<string | null>(null);
  // Until a write lands, the loaded row stands in for lastSyncedRef, projected onto the
  // columns of each save: permission-gated columns arrive after the load, so a
  // projection frozen at the first save would miss them and resend the same row.
  const loadedResponseRef = useRef<HeaderResponse | null>(null);
  const initialLoadDoneRef = useRef(false);
  const skipVisibilitySaveRef = useRef(false);
  const skipGroupBySaveRef = useRef(false);
  // The pins this hook last set itself (the mandatory reset, a restore). Only a change TO
  // them is skipped: a boolean skip-next outlived a load with nothing to restore — its
  // change landed before initialLoadDone — and swallowed the user's first pin.
  const ownPinnedRef = useRef<GridPinnedColumnFields | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const latestRef = useRef({
    headersRequest,
    processedColumns,
    effectiveColumnVisibilityModel,
    addHeader,
    updateHeader,
    groupByField,
    pinnedColumns,
  });
  latestRef.current = {
    headersRequest,
    processedColumns,
    effectiveColumnVisibilityModel,
    addHeader,
    updateHeader,
    groupByField,
    pinnedColumns,
  };

  const saveToApi = useCallback(() => {
    const {
      headersRequest: hr,
      processedColumns: pc,
      effectiveColumnVisibilityModel: ecvm,
      addHeader: add,
      updateHeader: update,
      groupByField: gbf,
      pinnedColumns: pc2,
    } = latestRef.current;
    if (!hr) return;

    const order = extractColumnOrderFromApi(apiRef, pc);
    const request = buildHeaderRequest({
      pageType: hr.pageType,
      resourceId: hr.resourceId,
      order,
      processedColumns: pc,
      effectiveColumnVisibilityModel: ecvm,
      groupByField: gbf,
      pinnedColumns: pc2,
    });

    const loaded = loadedResponseRef.current;
    const baseline =
      lastSyncedRef.current ??
      (loaded
        ? canonicalizeHeaderRequest(
            projectResponseAsRequest({
              pageType: hr.pageType,
              resourceId: hr.resourceId,
              response: loaded,
              processedColumns: pc,
            }),
          )
        : null);

    const canonical = canonicalizeHeaderRequest(request);
    if (canonical === baseline) {
      return;
    }

    // Only a confirmed write moves the baseline — a failed one leaves it stale so the
    // next schedule retries instead of skipping.
    const markSynced = () => {
      lastSyncedRef.current = canonical;
      loadedResponseRef.current = null;
    };

    if (savedHeaderIdRef.current) {
      update(savedHeaderIdRef.current, request)
        .then(markSynced)
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        .catch(() => {});
    } else {
      add(request)
        .then((response) => {
          if (response?.id) {
            savedHeaderIdRef.current = response.id;
          }
          markSynced();
        })
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        .catch(() => {});
    }
  }, [apiRef]);

  const scheduleSave = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(saveToApi, SAVE_DEBOUNCE_MS);
  }, [saveToApi]);

  useEffect(() => {
    setSavedOrder(null);
    setDragOrder(null);
    if (onPinnedColumnsChange) {
      const mandatory = mandatoryPinnedColumns ?? { left: [], right: [] };
      ownPinnedRef.current = mandatory;
      onPinnedColumnsChange(mandatory);
    }
    // A new pageType is a different row: nothing learned about the previous one applies.
    savedHeaderIdRef.current = null;
    loadedResponseRef.current = null;
    lastSyncedRef.current = null;

    if (!headersRequest) {
      setIsHeadersLoading(false);
      return;
    }

    const abortController = new AbortController();
    initialLoadDoneRef.current = false;
    setIsHeadersLoading(true);

    listHeaders(
      { pageType: headersRequest.pageType, resourceId: headersRequest.resourceId },
      abortController.signal,
    )
      .then((response) => {
        if (abortController.signal.aborted) return;

        if (response) {
          savedHeaderIdRef.current = response.id;
          loadedResponseRef.current = response;

          if (response.order?.length) {
            setSavedOrder(response.order);
          }

          if (onColumnVisibilityModelChange) {
            const visibilityModel = buildVisibilityModelFromResponse(response, processedColumns);
            if (visibilityModel) {
              skipVisibilitySaveRef.current = true;
              onColumnVisibilityModelChange(visibilityModel);
            }
          }

          if (onGroupByChange && response.extraDetails) {
            const persistedGroupBy = response.extraDetails.groupByField;
            if (latestRef.current.groupByField === null) {
              skipGroupBySaveRef.current = true;
              skipVisibilitySaveRef.current = true;
              onGroupByChange(
                typeof persistedGroupBy === 'string' ? fromBackendField(persistedGroupBy) : null,
              );
            }
          }

          if (onPinnedColumnsChange) {
            const restoredPinned = buildPinnedColumnsFromResponse(response);
            if (restoredPinned) {
              const mandatoryLeft = mandatoryPinnedColumns?.left ?? [];
              const mandatoryRight = mandatoryPinnedColumns?.right ?? [];
              const merged: GridPinnedColumnFields = {
                left: [
                  ...mandatoryLeft,
                  ...restoredPinned.left.filter((f) => !mandatoryLeft.includes(f)),
                ],
                right: [
                  ...mandatoryRight,
                  ...restoredPinned.right.filter((f) => !mandatoryRight.includes(f)),
                ],
              };
              ownPinnedRef.current = merged;
              onPinnedColumnsChange(merged);
            }
          }
        }

        initialLoadDoneRef.current = true;
        setIsHeadersLoading(false);
      })
      .catch(() => {
        if (abortController.signal.aborted) return;
        initialLoadDoneRef.current = true;
        setIsHeadersLoading(false);
      });

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headersRequest?.pageType, headersRequest?.resourceId]);

  useEffect(() => {
    if (!latestRef.current.headersRequest || !initialLoadDoneRef.current) return;
    if (skipVisibilitySaveRef.current) {
      skipVisibilitySaveRef.current = false;
      return;
    }
    scheduleSave();
  }, [effectiveColumnVisibilityModel, scheduleSave]);

  useEffect(() => {
    if (!headersRequest || !initialLoadDoneRef.current) return;
    if (skipGroupBySaveRef.current) {
      skipGroupBySaveRef.current = false;
      return;
    }
    scheduleSave();
  }, [groupByField, headersRequest, scheduleSave]);

  useEffect(() => {
    if (!headersRequest || !initialLoadDoneRef.current) return;
    const own = ownPinnedRef.current;
    ownPinnedRef.current = null;
    if (own && samePinned(own, pinnedColumns)) return;
    scheduleSave();
  }, [pinnedColumns, headersRequest, scheduleSave]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  return {
    savedOrder,
    dragOrder,
    setDragOrder,
    isHeadersLoading,
    scheduleSave,
  };
};

export default useHeadersBackendSync;

function samePinned(a: GridPinnedColumnFields, b: GridPinnedColumnFields | undefined): boolean {
  const same = (x: string[] = [], y: string[] = []) =>
    x.length === y.length && x.every((field, i) => field === y[i]);
  return !!b && same(a.left, b.left) && same(a.right, b.right);
}
